import * as Sentry from '@sentry/react';

const IGNORED_STACKS = [
  'cdn.amplitude.com',
  'cdn.cookielaw', // OneTrust Privacy
  'https://www.google.com/ccm/collect', // Google Analytics
  'n2av/', // Google Tag Gateway
  'yottaa',
];

export function initializeSentry() {
  if (!process.env.SENTRY_REACT_DSN) return;

  try {
    Sentry.init({
      beforeSend(event) {
        if (!event.exception) return event;

        const lastBreadcrumb = event.breadcrumbs?.at(-1) || {};

        // Ignore errors where the request failed or was canceled (status code 0).
        // These are network requests that never happen due to things like Ad Blockers
        // or CORS issues that we don't want to log.
        if (lastBreadcrumb.data?.status_code === 0) {
          return null;
        }

        //  Remove spammy errors that are not actionable.
        if (IGNORED_STACKS.find(stack => lastBreadcrumb.data?.url?.includes(stack))) {
          return null;
        }

        if (event.exception.values.every(value => value.mechanism.type === 'onerror')) {
          return null;
        } else {
          return event;
        }
      },
      debug: window.location.search?.includes('sentryDebug=true'),
      dsn: process.env.SENTRY_REACT_DSN,
      allowUrls: [
        /https?:\/\/((.*).herokuapp.com)/,
        /localhost/,
        /tempo-dev/,
        /https?:\/\/((.*).homechef.com)/,
        /https?:\/\/((.*).tempomeals.com)/,
        /webpack/,
      ],
      environment: RUNTIME_ENV /* global RUNTIME_ENV */,
      ignoreErrors: [
        /Load failed/,
        'Failed to fetch',
        'NetworkError',
        'Non-Error promise rejection captured with value',
        "Failed to execute 'fetch'",
      ],
      integrations: [
        Sentry.featureFlagsIntegration(),
        Sentry.replayIntegration({
          maskAllInputs: true,
          maskAllText: false,
          networkDetailAllowUrls: [/api\/v2/],
          networkRequestHeaders: ['Cookie', 'Referer'],
          blockAllMedia: false,
          block: ['.pac-container'], // .pac-container surrounds Google Autocomplete input
        }),
        Sentry.thirdPartyErrorFilterIntegration({
          filterKeys: ['m341h4nd'],
          behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        }),
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: process.env.SENTRY_REPLAY_ERROR_RATE || 0,
      tracesSampleRate: process.env.SENTRY_TRACES_SAMPLE_RATE || 0.01,
    });
    return true;
  } catch (e) {
    console.warn(`WARNING: Sentry initialization failed: ${e}`); // eslint-disable-line no-console
    return null;
  }
}

const sentry = initializeSentry();

// Unhandled promise rejections have no stack trace unless we extract it manually.
const handleUnhandledRejection = event => {
  if (!(event.promise instanceof Promise)) return;

  const errorMessage = event.reason?.message || event.reason || 'Unhandled promise rejection';
  const stackTrace = event.reason?.stack || 'No stack trace available';

  if (IGNORED_STACKS.find(stack => stackTrace.includes(stack))) return;

  Sentry.captureException(errorMessage, { contexts: { stackTrace: { frames: [stackTrace] } } });
};

window.addEventListener('unhandledrejection', event => handleUnhandledRejection(event));

export function setUser({ id, email, name }) {
  if (sentry) {
    Sentry.setUser({ id: id, email: email, username: name });
  }
}

export function captureException(error, context = {}) {
  if (sentry) {
    Sentry.captureException(error, { contexts: { 'Provided Context': context } });
  }
}

export function captureInfo(error, context = {}) {
  if (sentry) {
    Sentry.withScope(function (scope) {
      scope.setLevel('info');

      Sentry.captureException(error, { contexts: { 'Provided Context': context } });
    });
  }
}
